import { pathOr, pickAll } from 'ramda';
import type { HeaderBodyImageWithLink } from '../models.generated';
import { useHeaderBodyImageWithLinkQuery } from '../queries/contentTypes/headerBodyImageWithLink.generated';
import type { CtaWithUrlData } from './types';
import { transformCtaWithUrlData } from './useCtaWithUrlData';
import type { ImageType } from './useImageData';
import { transformImageData } from './useImageData';

export type HeaderBodyImageWithLinkItem = {
  key: HeaderBodyImageWithLink;
  eyebrow?: string;
  header: string;
  body?: string;
  image: ImageType;
  link: string;
  linkRef?: CtaWithUrlData;
  __typename: string;
};

export const isHeaderBodyImageWithLink = (
  data: object,
): data is HeaderBodyImageWithLinkItem => {
  return (
    data.hasOwnProperty('header') &&
    data.hasOwnProperty('image') &&
    data.hasOwnProperty('link')
  );
};

export const transformHeaderBodyImageWithLinkData = (
  response: object,
): HeaderBodyImageWithLinkItem => {
  const {
    key,
    eyebrow,
    header,
    body,
    image,
    link,
    linkRef,
    __typename,
  }: HeaderBodyImageWithLinkItem = pickAll(
    ['key', 'eyebrow', 'header', 'body', 'image', 'link', 'linkRef', '__typename'],
    pathOr({}, ['data', 'headerBodyImageWithLink'], response),
  );

  const transformedImageData = transformImageData({ data: { image } });

  return {
    key,
    eyebrow: eyebrow || undefined,
    header: header || `Header Body Image with Link: ${key}`,
    body: body || undefined,
    link: link || '',
    linkRef: linkRef
      ? transformCtaWithUrlData({ data: { ctaWithUrl: linkRef } })
      : undefined,
    ...transformedImageData,
    __typename,
  };
};

const useHeaderBodyImageWithLinkData = (
  id: HeaderBodyImageWithLink,
): HeaderBodyImageWithLinkItem => {
  const res = useHeaderBodyImageWithLinkQuery({ variables: { id } });

  return transformHeaderBodyImageWithLinkData(res);
};

export default useHeaderBodyImageWithLinkData;
